/**
 * useSecureMessages — React hook for E2E encrypted patient–provider messaging.
 *
 * Responsibilities:
 *  - Manages ECDH key pairs persisted in IndexedDB (never leaves the device).
 *  - Derives per-conversation AES-GCM-256 shared secrets from stored key pairs
 *    and the remote party's public key.
 *  - Encrypts outgoing messages/files before handing them to the API layer.
 *  - Decrypts incoming messages/files received from the API layer.
 *  - Rotates session keys on a configurable interval (default: every 50 messages
 *    or 24 hours, whichever comes first).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  SecureMessaging,
  type EncryptedMessage,
  type EncryptedFile,
} from '../lib/secure-messaging';

// ---------------------------------------------------------------------------
// IndexedDB key-store helpers
// ---------------------------------------------------------------------------

const DB_NAME = 'kaimed-secure-messaging-keys';
const DB_VERSION = 1;
const STORE_KEYPAIRS = 'keypairs';
const STORE_PUBLIC_KEYS = 'public-keys';

/** Shape of a persisted key-pair record. */
interface StoredKeyPair {
  /** Unique identifier — typically the local user's ID. */
  id: string;
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  /** ISO string marking when this key pair was created (for rotation logic). */
  createdAt: string;
}

/** Shape of a persisted remote public key record. */
interface StoredPublicKey {
  /** The remote participant's user ID. */
  id: string;
  publicKey: CryptoKey;
  /** ISO string marking when we last received this key. */
  receivedAt: string;
}

/**
 * Opens (or upgrades) the IndexedDB database that holds all key material.
 * Creates the required object stores on first run.
 */
async function openKeyStore(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_KEYPAIRS)) {
        db.createObjectStore(STORE_KEYPAIRS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_PUBLIC_KEYS)) {
        db.createObjectStore(STORE_PUBLIC_KEYS, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Persists a key pair to IndexedDB.
 * IndexedDB can natively store CryptoKey objects — no serialisation needed.
 */
async function saveKeyPair(record: StoredKeyPair): Promise<void> {
  const db = await openKeyStore();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_KEYPAIRS, 'readwrite');
    const req = tx.objectStore(STORE_KEYPAIRS).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Loads a key pair from IndexedDB by user ID.
 */
async function loadKeyPair(id: string): Promise<StoredKeyPair | null> {
  const db = await openKeyStore();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_KEYPAIRS, 'readonly');
    const req = tx.objectStore(STORE_KEYPAIRS).get(id);
    req.onsuccess = () => resolve((req.result as StoredKeyPair) ?? null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Persists a remote party's public key to IndexedDB.
 */
async function saveRemotePublicKey(record: StoredPublicKey): Promise<void> {
  const db = await openKeyStore();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PUBLIC_KEYS, 'readwrite');
    const req = tx.objectStore(STORE_PUBLIC_KEYS).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Loads a remote party's public key from IndexedDB.
 */
async function loadRemotePublicKey(remoteId: string): Promise<StoredPublicKey | null> {
  const db = await openKeyStore();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PUBLIC_KEYS, 'readonly');
    const req = tx.objectStore(STORE_PUBLIC_KEYS).get(remoteId);
    req.onsuccess = () => resolve((req.result as StoredPublicKey) ?? null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

// ---------------------------------------------------------------------------
// Rotation policy
// ---------------------------------------------------------------------------

/** Key rotation policy constants. */
const ROTATION_POLICY = {
  /** Rotate after this many messages sent with the current session key. */
  maxMessages: 50,
  /** Rotate after this many milliseconds regardless of message count (24 h). */
  maxAgeMs: 24 * 60 * 60 * 1000,
} as const;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Current state of the key exchange for a given conversation.
 */
export type KeyExchangeStatus =
  | 'idle'
  | 'generating'
  | 'awaiting-remote-key'
  | 'ready'
  | 'rotating'
  | 'error';

/**
 * Object returned by the {@link useSecureMessages} hook.
 */
export interface UseSecureMessagesReturn {
  /**
   * Whether E2E encryption is currently active for the given conversation.
   * Will be `false` when the remote party's public key has not yet been
   * registered via {@link registerRemotePublicKey}.
   */
  isE2EEnabled: boolean;

  /**
   * Current key-exchange status — use this to drive UI indicators.
   */
  keyStatus: KeyExchangeStatus;

  /**
   * The local user's public key as a JWK, ready to be sent to the server so
   * the remote party can perform their side of the ECDH exchange.
   * `null` until the key pair has been generated.
   */
  localPublicKeyJwk: JsonWebKey | null;

  /**
   * Registers the remote party's public key (received from the server).
   * After calling this the hook derives the shared secret and
   * {@link isE2EEnabled} becomes `true`.
   *
   * @param remoteUserId - The remote participant's user ID.
   * @param jwk          - The remote participant's ECDH public key as a JWK.
   */
  registerRemotePublicKey: (remoteUserId: string, jwk: JsonWebKey) => Promise<void>;

  /**
   * Encrypts a plaintext string with the current session key.
   *
   * @param plaintext - The message to encrypt.
   * @returns An {@link EncryptedMessage} or `null` if E2E is not yet ready.
   */
  encryptOutgoing: (plaintext: string) => Promise<EncryptedMessage | null>;

  /**
   * Decrypts an incoming ciphertext with the current session key.
   *
   * @param ciphertext - Base64-encoded ciphertext.
   * @param iv         - Base64-encoded IV.
   * @returns The decrypted plaintext or `null` if E2E is not ready / decryption fails.
   */
  decryptIncoming: (ciphertext: string, iv: string) => Promise<string | null>;

  /**
   * Encrypts a file attachment with the current session key.
   *
   * @param file - The File to encrypt.
   * @returns An {@link EncryptedFile} or `null` if E2E is not ready.
   */
  encryptOutgoingFile: (file: File) => Promise<EncryptedFile | null>;

  /**
   * Decrypts an encrypted file attachment.
   *
   * @param encryptedData - Raw encrypted bytes.
   * @param iv            - Base64-encoded IV.
   * @returns A Blob or `null` if E2E is not ready / decryption fails.
   */
  decryptIncomingFile: (encryptedData: ArrayBuffer, iv: string) => Promise<Blob | null>;

  /**
   * Triggers an immediate key rotation step.
   * The new local public key JWK must subsequently be sent to the server so
   * the remote party can update their side.
   */
  rotateSessionKeys: () => Promise<void>;

  /**
   * A human-readable error message when {@link keyStatus} is `'error'`.
   */
  errorMessage: string | null;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

/**
 * Provides E2E encrypted messaging primitives for a single conversation.
 *
 * @param localUserId      - The authenticated patient's user ID.
 * @param remoteUserId     - The provider's (or other party's) user ID.
 */
export function useSecureMessages(
  localUserId: string | null | undefined,
  remoteUserId: string | null | undefined,
): UseSecureMessagesReturn {
  const [keyStatus, setKeyStatus] = useState<KeyExchangeStatus>('idle');
  const [localPublicKeyJwk, setLocalPublicKeyJwk] = useState<JsonWebKey | null>(null);
  const [sharedSecret, setSharedSecret] = useState<CryptoKey | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Track rotation counters in a ref so changes don't cause unnecessary re-renders.
  const messagesSincRotation = useRef(0);
  const keyPairCreatedAt = useRef<number | null>(null);

  // ---------------------------------------------------------------------------
  // Key initialisation on mount (or when user IDs change)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!localUserId) return;

    let cancelled = false;

    const initKeys = async () => {
      setKeyStatus('generating');
      setErrorMessage(null);

      try {
        // Check whether we already have a key pair in IndexedDB for this user
        let stored = await loadKeyPair(localUserId);

        if (!stored) {
          // First run: generate and persist a new key pair
          const keyPair = await SecureMessaging.generateKeyPair();
          const now = new Date().toISOString();
          stored = {
            id: localUserId,
            privateKey: keyPair.privateKey,
            publicKey: keyPair.publicKey,
            createdAt: now,
          };
          await saveKeyPair(stored);
        }

        if (cancelled) return;

        // Export the public key so the UI can transmit it to the server
        const jwk = await SecureMessaging.exportPublicKey(stored.publicKey);

        if (cancelled) return;

        setLocalPublicKeyJwk(jwk);
        keyPairCreatedAt.current = new Date(stored.createdAt).getTime();

        // Attempt to derive the shared secret if the remote public key is cached
        if (remoteUserId) {
          const remoteStored = await loadRemotePublicKey(remoteUserId);
          if (remoteStored && !cancelled) {
            const secret = await SecureMessaging.deriveSharedSecret(
              stored.privateKey,
              remoteStored.publicKey,
            );
            if (!cancelled) {
              setSharedSecret(secret);
              setKeyStatus('ready');
            }
            return;
          }
        }

        if (!cancelled) {
          setKeyStatus('awaiting-remote-key');
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Key initialisation failed';
          setErrorMessage(msg);
          setKeyStatus('error');
        }
      }
    };

    void initKeys();

    return () => {
      cancelled = true;
    };
  }, [localUserId, remoteUserId]);

  // ---------------------------------------------------------------------------
  // Internal rotation implementation (shared by scheduler and public API)
  // ---------------------------------------------------------------------------

  const rotateSessionKeysInternal = useCallback(async () => {
    if (!localUserId || !remoteUserId) return;

    setKeyStatus('rotating');
    setErrorMessage(null);

    try {
      const stored = await loadKeyPair(localUserId);
      const remoteStored = await loadRemotePublicKey(remoteUserId);

      if (!stored || !remoteStored) {
        throw new Error('Cannot rotate: key material not found in IndexedDB');
      }

      const { newKeyPair, newSharedSecret } = await SecureMessaging.rotateKeys(
        stored.privateKey,
        remoteStored.publicKey,
      );

      const now = new Date().toISOString();
      const updatedRecord: StoredKeyPair = {
        id: localUserId,
        privateKey: newKeyPair.privateKey,
        publicKey: newKeyPair.publicKey,
        createdAt: now,
      };
      await saveKeyPair(updatedRecord);

      const newJwk = await SecureMessaging.exportPublicKey(newKeyPair.publicKey);
      setLocalPublicKeyJwk(newJwk);
      setSharedSecret(newSharedSecret);
      keyPairCreatedAt.current = Date.now();
      messagesSincRotation.current = 0;
      setKeyStatus('ready');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Key rotation failed';
      setErrorMessage(msg);
      setKeyStatus('error');
    }
  }, [localUserId, remoteUserId]);

  // ---------------------------------------------------------------------------
  // Rotation scheduler — poll once per minute, rotate when policy triggers
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (keyStatus !== 'ready' || !localUserId || !remoteUserId) return;

    const intervalId = window.setInterval(async () => {
      if (!keyPairCreatedAt.current) return;
      const ageMs = Date.now() - keyPairCreatedAt.current;
      if (ageMs >= ROTATION_POLICY.maxAgeMs) {
        await rotateSessionKeysInternal();
      }
    }, 60_000); // check every minute

    return () => window.clearInterval(intervalId);
  }, [keyStatus, localUserId, remoteUserId, rotateSessionKeysInternal]);

  // ---------------------------------------------------------------------------
  // Public API methods
  // ---------------------------------------------------------------------------

  const registerRemotePublicKey = useCallback(
    async (targetRemoteUserId: string, jwk: JsonWebKey): Promise<void> => {
      if (!localUserId) return;

      setKeyStatus('generating');
      setErrorMessage(null);

      try {
        const importedKey = await SecureMessaging.importPublicKey(jwk);

        await saveRemotePublicKey({
          id: targetRemoteUserId,
          publicKey: importedKey,
          receivedAt: new Date().toISOString(),
        });

        const stored = await loadKeyPair(localUserId);
        if (!stored) throw new Error('Local key pair not found — call initKeys first');

        const secret = await SecureMessaging.deriveSharedSecret(
          stored.privateKey,
          importedKey,
        );

        setSharedSecret(secret);
        setKeyStatus('ready');
        messagesSincRotation.current = 0;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to register remote public key';
        setErrorMessage(msg);
        setKeyStatus('error');
      }
    },
    [localUserId],
  );

  const encryptOutgoing = useCallback(
    async (plaintext: string): Promise<EncryptedMessage | null> => {
      if (!sharedSecret) return null;

      try {
        const encrypted = await SecureMessaging.encryptMessage(sharedSecret, plaintext);

        // Increment rotation counter and rotate if over threshold
        messagesSincRotation.current += 1;
        if (
          messagesSincRotation.current >= ROTATION_POLICY.maxMessages &&
          localUserId &&
          remoteUserId
        ) {
          void rotateSessionKeysInternal();
        }

        return encrypted;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Encryption failed';
        setErrorMessage(msg);
        return null;
      }
    },
    [sharedSecret, localUserId, remoteUserId, rotateSessionKeysInternal],
  );

  const decryptIncoming = useCallback(
    async (ciphertext: string, iv: string): Promise<string | null> => {
      if (!sharedSecret) return null;

      try {
        return await SecureMessaging.decryptMessage(sharedSecret, ciphertext, iv);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Decryption failed';
        setErrorMessage(msg);
        return null;
      }
    },
    [sharedSecret],
  );

  const encryptOutgoingFile = useCallback(
    async (file: File): Promise<EncryptedFile | null> => {
      if (!sharedSecret) return null;

      try {
        return await SecureMessaging.encryptFile(sharedSecret, file);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'File encryption failed';
        setErrorMessage(msg);
        return null;
      }
    },
    [sharedSecret],
  );

  const decryptIncomingFile = useCallback(
    async (encryptedData: ArrayBuffer, iv: string): Promise<Blob | null> => {
      if (!sharedSecret) return null;

      try {
        return await SecureMessaging.decryptFile(sharedSecret, encryptedData, iv);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'File decryption failed';
        setErrorMessage(msg);
        return null;
      }
    },
    [sharedSecret],
  );

  return {
    isE2EEnabled: keyStatus === 'ready',
    keyStatus,
    localPublicKeyJwk,
    registerRemotePublicKey,
    encryptOutgoing,
    decryptIncoming,
    encryptOutgoingFile,
    decryptIncomingFile,
    rotateSessionKeys: rotateSessionKeysInternal,
    errorMessage,
  };
}

export default useSecureMessages;
